// The Application API as web chat and the Cockpit use it: HTTP to the same origin, never a module
// (ADR-0012). The shapes follow the API's OpenAPI document (/api/openapi.json).
import { ApiError, call, type Fetch } from "./http.js";
import type { Language } from "./i18n/language.js";
import { indianGrouping } from "./i18n/format.js";
import { createHelpApi, type HelpApi } from "./help/api.js";
import { createModelsApi, type ModelsApi } from "./models/api.js";
import { createPrivacyApi, type PrivacyApi } from "./privacy/api.js";
import { createSecurityApi, type SecurityApi } from "./security/api.js";
import { createStatusApi, type StatusApi } from "./status/api.js";
import { createStepUpApi, type StepUpApi } from "./step-up/api.js";
import { createExpenseAccountsApi, type ExpenseAccountsApi } from "./settings/accounts/api.js";
import { createDimensionValuesApi, type DimensionValuesApi } from "./settings/dimensions/api.js";
import { createLedgerMappingApi, type LedgerMappingApi } from "./settings/ledger-mapping/api.js";
import { createBooksLinkApi, type BooksLinkApi } from "./documents/books-link-api.js";
import { createBooksReconciliationApi, type BooksReconciliationApi } from "./documents/books-reconciliation-api.js";

export { ApiError };

export interface Session {
  readonly tenantId: string;
  readonly personId: string;
  readonly roles: readonly string[];
  /** The business's name: its supplier legal name once set, otherwise the name it was given (#343). */
  readonly businessName?: string;
}

/** What a person chose to be emailed about their work, and where (#512). Both off until they switch one on. */
export interface StaffNoticeChoice {
  readonly email: string | null;
  readonly newWorkItems: boolean;
  readonly dailyDigest: boolean;
}

/** The choice as kept: nothing is emailed until its address is confirmed with the code sent to it (#655). */
export interface StaffNoticeView extends StaffNoticeChoice {
  readonly emailConfirmed: boolean;
  /** A code emailed to the address waits to be entered; with none pending, saving gets one (#686). */
  readonly codePending: boolean;
  /** Saved, but no code went: the person has had as many as LAMP sends in an hour, so it comes on a save after (#718). */
  readonly codeHeld?: true;
}

/** The signed-in person's own profile, as `GET /api/me` answers. */
export interface Me {
  readonly personId: string;
  readonly tenantId: string;
  readonly name: string | null;
  readonly roles: readonly string[];
  /** The language LAMP speaks to them, wherever they sign in. */
  readonly language: Language;
}

export interface OrderLine {
  readonly item: string;
  readonly quantity: number;
  readonly unit: string;
  /** Absent when the person's Roles may not see customer pricing. */
  readonly unitPrice?: number;
  readonly amount?: number;
}

export interface InvoicePreview {
  readonly kind: "invoice";
  readonly number: string;
  readonly date: string;
  readonly supplier: { readonly name: string; readonly gstin: string };
  readonly customer: { readonly name: string; readonly gstin: string | null; readonly address: string };
  readonly supplyType: "intra_state" | "inter_state";
  readonly placeOfSupply: string;
  readonly lines: readonly {
    readonly description: string;
    readonly hsn: string;
    readonly quantity: number;
    readonly unit: string;
    readonly unitPrice: number;
    readonly taxableValue: number;
    readonly rate: number;
    /** Where the rate came from: LAMP's rate for the HSN, or the one stated on the Item where LAMP has none. */
    readonly rateFrom?: "pack" | "item";
    readonly cgst: number;
    readonly sgst: number;
    readonly igst: number;
  }[];
  readonly taxableValue: number;
  readonly cgst: number;
  readonly sgst: number;
  readonly igst: number;
  readonly roundOff: number;
  readonly total: number;
  /** An advance already received, deducted from what the customer still owes. */
  readonly advance?: number;
  /** The customer's GSTIN as the GST system last answered for it. */
  readonly gstinCheck?: { readonly status: "active" | "cancelled" | "suspended"; readonly checkedOn: string };
  /** The vehicle the goods leave in, for the e-way bill. */
  readonly vehicle?: string;
  readonly problems: readonly string[];
  /** What to know before confirming, such as LAMP's GST rate charged over a different one on an Item. */
  readonly notes?: readonly string[];
  readonly document?: { readonly id: string; readonly pdf: string };
}

/** A message LAMP drafted to someone outside the business: editable before it goes, or Call instead. */
export interface MessagePreview {
  readonly kind: "message";
  readonly to: { readonly name: string; readonly phone?: string };
  readonly channel: string;
  readonly text: string;
}

/**
 * A quotation as it will be sent to the customer: its prices only for a Role that may see customer pricing, and its
 * margin only for one that may see costs and margins.
 */
export interface QuotationPreview {
  readonly kind: "quotation";
  readonly number: string;
  readonly date: string;
  readonly validUntil: string;
  readonly customer: string;
  /** The customer's recorded contact it goes to. */
  readonly to: string;
  readonly lines: readonly OrderLine[];
  readonly total?: number;
  readonly marginPct?: number;
  /** The covering note sent with it, when LAMP wrote one. */
  readonly coverNote?: string;
}

/** An enquiry's acknowledgement as it will be sent to the customer: when the quotation will follow. */
export interface AcknowledgementPreview {
  readonly kind: "acknowledgement";
  readonly enquiry: string;
  readonly customer: string;
  /** The customer's recorded contact it goes to. */
  readonly to: string;
  readonly description: string;
  /** When it promises the quotation, an ISO date-time. */
  readonly quotationBy: string;
}

export type Preview =
  | {
      readonly kind: "sales_order";
      readonly number: string;
      readonly date: string;
      readonly customer: string;
      readonly lines: readonly OrderLine[];
      readonly total?: number;
      /** The date it is promised to the customer by, as the order said it or a person set it. */
      readonly committedDate?: string;
      /** The customer's purchase order number, when it was read from their purchase order. */
      readonly customerPo?: string;
      /** The customer's standing Notes, when the person's Roles may read Notes (#510). */
      readonly notes?: readonly string[];
    }
  | {
      readonly kind: "stock_issue";
      readonly lines: readonly OrderLine[];
      /** Who the goods are for, with their standing Notes, when the person's Roles may read Notes (#510). */
      readonly customer?: string;
      readonly notes?: readonly string[];
    }
  | {
      /** The goods handed over with their documents: who they go to and their standing Notes (#510). */
      readonly kind: "dispatch";
      readonly customer: string;
      readonly notes: readonly string[];
    }
  | InvoicePreview
  | MessagePreview
  | QuotationPreview
  | AcknowledgementPreview
  | CreditNotePreview
  | BomFormPreview
  | RoutingFormPreview
  | DrawingReviewPreview
  | JournalsPreview;

/**
 * The journals a month's close prepared, as Confirm posts them (#1013): each one's entries, account by account, and the
 * working it was prepared from, line by line (the closing stock's each Item's quantity, rate and value, and its total).
 */
export interface JournalsPreview {
  readonly kind: "journals";
  readonly journals: readonly {
    readonly id: string;
    readonly date: string;
    readonly narration: string;
    /** In paise. */
    readonly lines: readonly {
      readonly account: string;
      readonly name: string;
      readonly side: "debit" | "credit";
      readonly amount: number;
    }[];
    readonly reversesOn: string | null;
    readonly working: readonly string[];
  }[];
}

/** A drawing under review (4.1.1.1): its revision, whose it is, its file and what LAMP read from it. */
export interface DrawingReviewPreview {
  readonly kind: "drawing_review";
  readonly drawing: string;
  readonly revision: string;
  readonly customer: string | null;
  readonly file: string | null;
  readonly read: readonly { readonly label: string; readonly value: string }[];
}

/** A BOM LAMP drafted from a released drawing, for the Floor Supervisor to correct in its Form and confirm. */
export interface BomFormPreview {
  readonly kind: "bom";
  readonly drawing: string;
  readonly revision: string;
  /** The part's Item; null for a new part, which the Form names. */
  readonly item: { readonly id: string; readonly name: string } | null;
  readonly lines: readonly {
    readonly component: string;
    readonly name: string;
    readonly quantity: number;
    readonly unit: string;
    readonly scrap_pct: number;
  }[];
  /** What LAMP could not match to anything on record. */
  readonly unmatched: readonly string[];
  /** The Items a line may name, with the units each is counted in. */
  readonly items: readonly { readonly id: string; readonly name: string; readonly units: readonly string[] }[];
}

/** A routing LAMP drafted from a released drawing, for the Floor Supervisor to correct in its Form and confirm. */
export interface RoutingFormPreview {
  readonly kind: "routing";
  readonly drawing: string;
  readonly revision: string;
  readonly operations: readonly {
    readonly sequence: number;
    readonly name: string;
    readonly work_centre: string | null;
    readonly job_worker: string | null;
    readonly setup_minutes: number;
    readonly cycle_minutes: number;
  }[];
  readonly unmatched: readonly string[];
  readonly work_centres: readonly { readonly id: string; readonly name: string }[];
}

/** A file attached to a card's Form (#707): a PDF, JPEG or PNG, its bytes base64-encoded. */
export interface CardFile {
  readonly mediaType: "application/pdf" | "image/jpeg" | "image/png";
  readonly data: string;
}

/** A card's Form as the person filled it in: a drafted BOM's lines or a routing's operations, corrected. */
export type CardForm = Readonly<
  Record<string, string | number | boolean | readonly Readonly<Record<string, string | number | boolean>>[] | CardFile>
>;

/** A credit note for returned goods as it will be issued (M3-03c): the Document LAMP prepared, line for line. */
export interface CreditNotePreview {
  readonly kind: "credit_note";
  readonly number: string;
  readonly date: string;
  readonly invoice: string;
  readonly customer: string;
  readonly lines: readonly {
    readonly item: string;
    readonly quantity: number;
    readonly unit: string;
    readonly amount: number;
    readonly cgst: number;
    readonly sgst: number;
    readonly igst: number;
  }[];
  readonly roundOff: number;
  readonly total: number;
}

export type CardAction = "confirm" | "send_back" | "done" | "retry";

/** Something that failed, named with what happened and how it recovers. */
export interface Failure {
  readonly problem: string;
  /** What to do next; empty when the problem already says it, as a read-only business's notice does. */
  readonly recovery: string;
}

/**
 * A field a Task's Done (or a drafted order's Confirm) asks for, sent back as the card's Form: what its Output Contract
 * needs from the person.
 */
export interface CardAsk {
  readonly name: string;
  /** A line of words, a date, a number, one of a few options, or a table of rows. */
  readonly type: "text" | "date" | "number" | "choice" | "rows" | "file";
  readonly required: boolean;
  /** What the field holds already, to keep or correct. */
  readonly value?: string;
  /** What the field is called, in plain words. */
  readonly label?: string;
  /** A choice's options: words sent back as they are, or records shown by `label` and sent back by `value`. */
  readonly options?: readonly CardOption[];
  /** A table's columns, each a field of one row. */
  readonly columns?: readonly CardAsk[];
  /** The rows a table holds already, to keep, edit, add to or remove. */
  readonly rows?: readonly Readonly<Record<string, string>>[];
  /** A table whose lines split an amount: `column` holds each line's share in rupees, `total` the whole in paise. */
  readonly split?: { readonly column: string; readonly total: number };
  /** A table column shown as on record, not edited. */
  readonly readOnly?: true;
  /** A table whose lines are those it holds: one may be removed, none added. */
  readonly fixed?: true;
}

/**
 * One option of a card's choice: a word, or a record by its name sent back by its id (#607), with what tells it apart
 * from another of the same name when it says (#658).
 */
export type CardOption = string | { readonly value: string; readonly label: string; readonly detail?: string };

/**
 * An option as a card lists it: a word in words, a record by its name, and a record another of the same name (whatever
 * its case) shares it with says what tells it apart (#658), with its id too when that is the same (#1070). Telegram
 * shows a choice the same way: tests/cards keeps the two one.
 */
export const shownOption = (option: CardOption, options: readonly CardOption[]): string => {
  if (typeof option === "string") return option.replaceAll("_", " ");
  const same = (o: CardOption) => typeof o !== "string" && o.label.toLowerCase() === option.label.toLowerCase();
  const apart = (o: { readonly value: string; readonly detail?: string }) => o.detail ?? o.value;
  const named = options.filter(same);
  if (named.length < 2) return option.label;
  const alike = named.filter((o) => typeof o !== "string" && apart(o) === apart(option)).length > 1;
  return alike ? `${option.label} (${apart(option)}, ${option.value})` : `${option.label} (${apart(option)})`;
};

/**
 * Rupees as typed ("30,000", "₹4999.50") in paise; undefined when it is not an amount ("1e5", "-500") or too large to
 * count exactly. The Application API reads a split's amounts the same way (#658): tests/cards keeps the two one.
 */
export const typedPaise = (typed: string): number | undefined => {
  const plain = typed.trim().replace(/^₹\s*/, "").replaceAll(",", "");
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(plain);
  if (!match) return undefined;
  const [, whole = "0", fraction = ""] = match;
  const paise = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  return Number.isSafeInteger(paise) ? paise : undefined;
};

/** An email a card's Confirm sends a Counterparty, word for word; it waits UNDO_SECONDS before it goes. */
export interface CardEmail {
  readonly counterparty: string;
  /** Their contact email on record; null when there is none, and the Confirm asks for one. */
  readonly to: string | null;
  readonly subject: string;
  readonly text: string;
}

export interface Card {
  readonly run: string;
  readonly workItem: string;
  readonly task: string;
  readonly name: string;
  readonly kind: "ask" | "do" | "blocked" | "working" | "done";
  readonly status: "open" | "awaiting_approval" | "blocked" | "done";
  readonly actions: readonly CardAction[];
  readonly problems: readonly string[];
  readonly preview?: Preview;
  /** The email its Confirm sends a customer, supplier or job worker, as it will go (#512). */
  readonly email?: CardEmail;
  /** Why it is blocked, when a step failed (e.g. the e-way bill portal did not answer). */
  readonly failure?: Failure;
  /** What its Done asks for. */
  readonly asks?: readonly CardAsk[];
  /** Why it offers no Done: the Task is not done in LAMP yet, and where it is. */
  readonly elsewhere?: string;
  /** On an order held on credit: the customer's overdue invoices, each opening as its PDF. */
  readonly overdue?: readonly { readonly number: string; readonly pdf: string }[];
  /** The papers the Task is done against, each opening as its file: a supplier's bill as it was attached. */
  readonly files?: readonly { readonly name: string; readonly href: string }[];
  /** On a material check waiting for a part's BOM: the Form that gives it ("Give the BOM", #565). */
  readonly bom?: PartBom;
}

/**
 * A card action's answer: the thread, and whether its Confirm queued an email to a Counterparty, now waiting out its undo
 * window (#718). The web shows the countdown by this, never by LAMP's words, which may be in Hindi.
 */
export type ActReply = readonly ChatEntry[] & { readonly emailQueued?: boolean };

export interface ChatEntry {
  readonly id: string;
  readonly author: "person" | "lamp";
  readonly text: string;
  readonly at: string;
  readonly card?: Card;
  /** The Run the message is about, to open, when it shows no card: an order's status (#510). */
  readonly run?: string;
  /** The Tree Change the message previews, as it now stands. */
  readonly treeChange?: TreeChange;
  /** A file the person sent in this message (a Received File). */
  readonly file?: { readonly id: string; readonly name: string; readonly href: string };
  /** The Received File LAMP asks or answered about in this message. */
  readonly receivedFile?: ReceivedFile;
  /** LAMP offers to add the customer or Item an order named and the business lacks, then draft the order. */
  readonly offer?: "add_them";
  /** The customer, supplier, job worker or Item LAMP drafted from the person's words, to confirm or correct. */
  readonly masterData?: MasterDataCard;
  /** The part's BOM and routing LAMP drafted from the person's words, to confirm (#565). */
  readonly bom?: BomDraftCard;
}

/** What a master record is: a Counterparty of one kind, or an Item. */
export type MasterDataRecord = CounterpartyKind | "item";

/** What an Item is to the business. */
export const ITEM_KINDS = ["finished_part", "raw_material", "consumable"] as const;
export type ItemKind = (typeof ITEM_KINDS)[number];

/** A Counterparty's or Item's particulars as a page or a correction sends them; the API checks each one. */
export interface MasterDataFields {
  readonly name?: string;
  readonly gstin?: string;
  readonly address?: string;
  /** A GST State code or name. */
  readonly state?: string;
  readonly phone?: string;
  readonly email?: string;
  readonly paymentTermsDays?: number;
  /** What an Item is: a finished part, raw material or a consumable. */
  readonly itemKind?: ItemKind;
  readonly unit?: string;
  readonly hsn?: string;
  /** An Item's GST rate in percent: its invoices use it where LAMP has no rate for its HSN. */
  readonly gstRate?: number;
  /** The supplier an Item is bought from, by name. */
  readonly supplier?: string;
  /** Posted once, as the Item's opening Stock Count, in `unit` or else the Item's own unit. */
  readonly openingStock?: { readonly quantity: number; readonly unit?: string };
  /** A supplier's PAN: kept only on a person's word, and only the PAN its GSTIN carries. */
  readonly pan?: string;
  /** A supplier's usual Nature of Payment for TDS on its bills, e.g. contract_work. */
  readonly natureOfPayment?: string;
  readonly tdsCertificate?: TdsCertificate;
}

/** A lower-deduction certificate a supplier holds: TDS at `rate` percent on up to `limit` paise, `from` to `to`. */
export interface TdsCertificate {
  readonly number: string;
  readonly rate: number;
  readonly limit: number;
  readonly from: string;
  readonly to: string;
}

/** A supplier's tax particulars as its page shows them (#617). */
export interface TaxParticulars {
  /** In full to a person whose Roles hold the ID numbers class, else masked to its last four characters. */
  readonly pan: string | null;
  readonly panMasked: boolean;
  readonly natureOfPayment: string | null;
  readonly tdsCertificate: TdsCertificate | null;
  /** The Natures of Payment to choose the usual one from, each in words. */
  readonly natures: readonly { readonly id: string; readonly words: string }[];
  readonly mayChange: boolean;
}

/** A correction to a draft in chat: each field given replaces the drafted one, and `null` clears it from the draft. */
export type MasterDataCorrections = { readonly name?: string } & {
  readonly [K in Exclude<keyof MasterDataFields, "name">]?: MasterDataFields[K] | null;
};

/** The fields a page's correction clears on record when the person empties them. */
const CLEARABLE_FIELDS: readonly (keyof MasterDataFields)[] = [
  "gstin",
  "address",
  "state",
  "phone",
  "email",
  "paymentTermsDays",
  "itemKind",
  "hsn",
  "gstRate",
  "pan",
  "natureOfPayment",
  "tdsCertificate",
];

/** The fields a correction form's person emptied, each as a clear (null) where the record lets it be cleared. */
export const clearing = (emptied: readonly (keyof MasterDataFields)[]): MasterDataCorrections =>
  Object.fromEntries(emptied.filter((field) => CLEARABLE_FIELDS.includes(field)).map((field) => [field, null]));

/** A part's bill of materials and routing as a Form shows them (#565): drafted, as on record, or empty. */
export interface PartBom {
  readonly part: { readonly id: string; readonly name: string; readonly unit: string };
  readonly lines: readonly {
    readonly component: string;
    readonly name: string;
    readonly quantity: number;
    readonly unit: string;
    readonly scrap_pct?: number;
  }[];
  readonly operations: readonly { readonly name: string; readonly outsourced: boolean; readonly job_worker?: string }[];
  /** The Items a line may name, with the units each is kept in or converts from. */
  readonly items: readonly { readonly id: string; readonly name: string; readonly units: readonly string[] }[];
  /** The job workers an operation may be sent to. */
  readonly jobWorkers: readonly { readonly id: string; readonly name: string }[];
}

/** A part's BOM and routing as its Item's page reads them, and what the person's Roles let them do with them. */
export type ItemBomRead = PartBom & {
  /** False when the person may not read them: none is shown, which is not none on record (#668). */
  readonly mayRead: boolean;
  readonly mayRecord: boolean;
};

/** A part's BOM and routing as the person sends them back from its Form. */
export interface PartBomGiven {
  readonly lines: readonly {
    readonly component: string;
    readonly quantity: number;
    readonly unit: string;
    readonly scrap_pct?: number;
  }[];
  readonly operations: readonly {
    readonly name: string;
    readonly outsourced?: boolean;
    readonly job_worker?: string;
  }[];
}

/** A BOM and routing LAMP drafted from the person's words (#565): open until it is confirmed. */
export interface BomDraftCard {
  readonly message: string;
  readonly status: "open" | "done";
  readonly form: PartBom;
}

/** A draft in the thread, as LAMP read it: open until it is confirmed. */
export interface MasterDataCard {
  readonly message: string;
  readonly status: "open" | "done";
  readonly draft: MasterDataFields & {
    readonly record: MasterDataRecord;
    readonly action: "add" | "correct";
    readonly name: string;
  };
}

/** A master record added or corrected from its page: its id, and what LAMP did in words. */
export interface MasterDataSaved {
  readonly id: string;
  readonly text: string;
}

/** The kinds of file LAMP reads. */
export const FILE_KINDS = [
  "customer_po",
  "supplier_bill",
  "sales_invoice",
  "stock_register",
  "drawing",
  "joining_form",
  "attendance_register",
  "gate_pass",
  "delivery_challan",
  "job_work_return",
  "stock_count",
  "bank_statement",
  "complaint_note",
] as const;
export type FileKind = (typeof FILE_KINDS)[number];

/** What a file LAMP does not read may be kept against. */
export const KEPT_AGAINST = ["customer", "supplier", "item", "sales_order", "purchase_order", "run"] as const;
export type KeptAgainst = (typeof KEPT_AGAINST)[number];

/** A file kept against a record, as its page lists it: opened from `href`, with the person's own words. */
export interface KeptFileRow {
  readonly name: string;
  readonly href: string;
  /** The person's own line about it. Untrusted content: shown, never followed. */
  readonly words: string;
  /** Who kept it, by their name; null when they have none. */
  readonly keptBy: string | null;
  readonly keptAt: string;
  readonly run?: string;
}

/** A file handed to LAMP, as the thread shows it: the kind LAMP proposes and what the person may do, or what it read. */
export interface ReceivedFile {
  readonly id: string;
  readonly name: string;
  readonly href: string;
  readonly state: "confirm" | "ask" | "what_is_it" | "failed" | "refused" | "previewed" | "done";
  readonly kind?: FileKind;
  readonly party?: string | null;
  /** The kind the person's words named, when it disagrees with the file. */
  readonly named?: FileKind;
  readonly failure?: "model" | "unreadable" | "password" | "empty";
  readonly choices: readonly FileKind[];
  readonly actions: readonly ("yes" | "choose" | "retry" | "record" | "develop" | "keep" | "send_back")[];
  readonly draft?: Readonly<Record<string, unknown>>;
  /** A recorded drawing whose part can be developed in one tap. */
  readonly develop?: { readonly drawing: string; readonly customer: string };
  /** The Run developing the part, once started, or the Run a kept file shows on. */
  readonly run?: string;
  /** The file as data to bring in: what confirming brings in, and what it leaves out and why. */
  readonly dataImport?: DataImportPreview;
  /** Recording it, or sending it back, needs the person's reason: a gate entry held for a decision. */
  readonly reasonNeeded?: true;
  /** The record the file was kept against, unread, with the person's own words. */
  readonly kept?: { readonly object: KeptAgainst; readonly label: string; readonly words: string };
  /** A held gate entry sent back: the person's reason. */
  readonly sentBack?: string;
  /** What a customer's purchase order names that is not on record: recording it adds them and drafts the order. */
  readonly missing?: { readonly customer?: string; readonly items: readonly string[] };
}

/** A Tally export or a sheet of masters, previewed as a Data Import: confirmed once, by someone who approves imports. */
export interface DataImportPreview {
  readonly id: string;
  readonly number: string;
  readonly status: "previewed" | "confirmed";
  readonly asOf: string;
  /** The customers, suppliers, job workers and Items new to LAMP, which confirming creates. */
  readonly customers: number;
  readonly suppliers: number;
  readonly jobWorkers: number;
  readonly items: number;
  readonly newCounterparties: number;
  readonly newItems: number;
  /** Counterparties and Items LAMP has already, which an opening balance or opening stock is only linked to. */
  readonly linkedCounterparties: number;
  readonly linkedItems: number;
  readonly openingBalances: number;
  readonly receivablePaise: number;
  readonly payablePaise: number;
  /** Each Opening Balance confirming posts: paise the party owes the business, or the business owes it. */
  readonly balanceLines: readonly {
    readonly name: string;
    readonly receivablePaise: number;
    readonly payablePaise: number;
  }[];
  readonly openingStockLines: number;
  /** Each opening Stock Receipt confirming posts, in the Item's unit. */
  readonly stockLines: readonly { readonly name: string; readonly quantity: number; readonly unit: string }[];
  /** Each party whose PAN the file gives, masked to its last four characters (#617). */
  readonly panLines?: readonly { readonly name: string; readonly pan: string }[];
  /** Lines LAMP has already with nothing new, left out so nothing is added twice. */
  readonly already: number;
  /** What the file held that LAMP couldn't read or won't bring in, row by row, with why. */
  readonly notBrought: readonly { readonly what: string; readonly name: string; readonly reason: string }[];
  /** Whether confirming would bring anything in. */
  readonly ready: boolean;
}

/** Where a month's Accounting Export to Tally downloads from (`YYYY-MM`). */
export const tallyExportHref = (month: string) => `/api/exports/tally?month=${month}`;

/** What the person corrected in a drawing's preview before recording it. */
export interface DrawingCorrections {
  readonly number?: string;
  readonly revision?: string;
  readonly customer?: string;
  /** Why a gate entry held for a decision is recorded anyway. */
  readonly reason?: string;
}

/** A file to send, read in the browser. */
export interface FileToSend {
  readonly name: string;
  readonly mediaType: string;
  /** Its bytes, base64-encoded. */
  readonly data: string;
}

export interface Decision {
  readonly run: string;
  readonly workItem: string;
  readonly task: string;
  readonly name: string;
  readonly kind: "confirm" | "do" | "fix";
  readonly reason: string;
  /** "More about this": what the Task is for, who usually does it and why it is waiting. */
  readonly details: readonly string[];
  /** The Task's, step's and Value Stream's codes, for those who want them: opened from "More about this". */
  readonly codes?: string;
  /** What its Run is about (#513): "SO/26-27/0003 · Bharat Hydraulics · 80 Hydraulic piston rod". */
  readonly subject?: string;
  readonly amount?: { readonly paise: number; readonly label: "overdue" | "to bill" | "order value" };
  /** Who the decision is about; the phone, when LAMP has one, is for Call. */
  readonly counterparty?: { readonly name: string; readonly phone?: string };
  /**
   * A confirmation's or a block's own card, with its preview and actions as chat shows it, whichever Channel or
   * person started the Run: reviewed and decided right there on Today.
   */
  readonly card?: Card;
  /** What the person may choose besides marking it done: the rate revision, on a margin at risk the customer's change caused. */
  readonly offers?: readonly "rate_revision"[];
  /** What Mark as done asks for. */
  readonly asks?: readonly CardAsk[];
  /** The papers a Task of the person's own is done against, each opening as its file. */
  readonly files?: readonly { readonly name: string; readonly href: string }[];
  /** Why it offers no Mark as done: the Task is not done in LAMP yet, and where it is. */
  readonly elsewhere?: string;
  /** Past its Task's expected time: the overdue ladder has nudged. */
  readonly late?: true;
  /** On an Owner's Today: the Role it waits on, which no one holds, so it comes to them; `says` marks it so. */
  readonly noOneHolds?: { readonly role: string; readonly says: string };
  /**
   * On an incident or near miss: what was reported, each line labelled in the person's language and in the reporter's
   * own words.
   */
  readonly report?: readonly {
    readonly field: "kind" | "what_happened" | "where" | "when" | "reported_by";
    readonly label: string;
    readonly text: string;
  }[];
}

export interface Today {
  readonly headline: string;
  readonly decisions: readonly Decision[];
  readonly issued: readonly {
    /** The Run that issued it, when a Run did. */
    readonly run?: string;
    readonly object: string;
    readonly id: string;
    readonly number: string | null;
    readonly pdf: string;
  }[];
  /** What LAMP noticed that the person may see, such as a key account gone quiet; absent when nothing is. */
  readonly noticed?: readonly Suggestion[];
  /** The person's Reminders whose day has come, set in chat (#510); absent when none. */
  readonly reminders?: readonly {
    readonly id: string;
    readonly text: string;
    readonly on: string;
    /** The colleague who set it, when it was not the person. */
    readonly from?: string;
  }[];
}

/** A live Run on the Floor, on the step it stands on. */
export interface FloorRun {
  readonly run: string;
  /** What the Run is about: its Counterparty, sales order or invoice number, or its short id. */
  readonly title: string;
  /** The Work Item it waits on, if any. */
  readonly waitingOn: string | null;
  /** It waits on the person looking: the only thing outlined amber. */
  readonly needsYou: boolean;
  readonly late: boolean;
}

/** One Value Stream's card on the Floor, as `GET /api/floor` answers. */
export interface FloorCard {
  readonly valueStream: string;
  readonly name: string;
  /** The Value Driver it moves. */
  readonly driver: string | null;
  readonly live: number;
  readonly needYou: number;
  readonly late: number;
  readonly steps: readonly { readonly step: string; readonly name: string; readonly runs: readonly FloorRun[] }[];
}

/** A record that moved a KPI most in the latest month, with why in a line. */
export interface InsightsContributor {
  /** The Business Object it is, e.g. `invoice`. */
  readonly object: string;
  readonly record: string;
  readonly reason: string;
  /** The Run it belongs to, when there is one the person may see. */
  readonly run: string | null;
}

/** A month, `YYYY-MM`. */
export type Month = string;

export type InsightsKpi =
  | {
      readonly name: string;
      readonly unit: string;
      readonly status: "measured";
      /** One per month asked, oldest first; null for a month with nothing to compute it from. */
      readonly values: readonly { readonly month: Month; readonly value: number | null }[];
      readonly contributors: readonly InsightsContributor[];
    }
  | {
      readonly name: string;
      readonly unit: string | null;
      readonly status: "not_measured";
      /** What it needs to be measured. */
      readonly reason: string;
      readonly contributors: readonly InsightsContributor[];
    };

/** Insights, as `GET /api/insights` answers: the Owner's ranked sub-drivers first. */
export interface Insights {
  /** The months asked, oldest first. */
  readonly months: readonly Month[];
  readonly subDrivers: readonly {
    readonly id: string;
    readonly driver: string;
    readonly plain_name: string;
    readonly kpis: readonly InsightsKpi[];
  }[];
}

/** One page of a Cockpit list, filtered and paged by the Application API. */
export interface ListPage<T> {
  readonly rows: readonly T[];
  /** Rows matching the filters, across every page. */
  readonly total: number;
  /** From 1. */
  readonly page: number;
  readonly pages: number;
}

export type RunShow = "all" | "live" | "waiting" | "finished";

/** A Run on the Runs page. */
export interface RunRow {
  readonly run: string;
  /** What the Run is about: its Counterparty, sales order or invoice number, or its short id. */
  readonly title: string;
  readonly valueStream: string | null;
  /** The step it stands on, by name. */
  readonly step: string | null;
  readonly status: "active" | "blocked" | "paused" | "halted" | "completed";
  readonly waitingOn: string | null;
  /** It waits on the person looking. */
  readonly needsYou: boolean;
  readonly late: boolean;
}

/** An issued Document on the Documents page. */
export interface DocumentRow {
  readonly id: string;
  readonly kind: "invoice";
  readonly number: string | null;
  readonly date: string | null;
  readonly counterparty: string | null;
  /** In paise; absent when the person's Roles may not see customer pricing. */
  readonly amount?: number;
  /** Its PDF, when the person may open it. */
  readonly pdf: string | null;
}

export type CounterpartyKind = "customer" | "supplier" | "job_worker";

/** A Counterparty on the Customers and suppliers page. */
export interface CounterpartyRow {
  readonly id: string;
  readonly name: string;
  readonly kind: string;
  readonly gstin: string | null;
  readonly phone: string | null;
  readonly paymentTermsDays: number | null;
  /** What its edit form starts from. */
  readonly address?: string | null;
  readonly email?: string | null;
  /** Its GST State code. */
  readonly state?: string | null;
}

export type StockShow = "all" | "in_stock" | "out_of_stock";

/** An Item on the Items and stock page. */
export interface ItemRow {
  readonly id: string;
  readonly name: string;
  readonly unit: string;
  readonly hsn: string | null;
  /** The GST rate in percent stated on the Item, if any. */
  readonly gstRate: number | null;
  /** A finished part, raw material or a consumable, when the business said which. */
  readonly kind: ItemKind | null;
  /** On hand, if the person may read the stock Ledger. */
  readonly stock: number | null;
  /** What confirmed orders not yet picked hold of it, if the person may read the stock Ledger (#1013). */
  readonly heldForOrders?: number | null;
  /**
   * What it costs (to buy, or to make) and sells for, in paise per its unit (#781): null where none is kept, left out
   * where the person's Roles do not show it (#1013).
   */
  readonly purchaseRate?: number | null;
  readonly salePrice?: number | null;
  /** The free stock at which a purchase requisition for it is raised, and how much that asks for, in its unit. */
  readonly reorderLevel?: number | null;
  readonly minOrderQuantity?: number | null;
}

/** One thing the top bar's search found. */
export interface SearchResult {
  readonly kind: "order" | "counterparty" | "document";
  readonly id: string;
  readonly title: string;
  readonly detail: string;
  /** The order's Run, if the person may see it. */
  readonly run: string | null;
  /** The Document's PDF, if the person may open it. */
  readonly pdf: string | null;
}

export type ParameterValue = number | boolean | string;

/** One Parameter the Business Tree declares, as Settings shows it. */
export interface SettingsParameter {
  readonly node: string;
  readonly nodeName: string;
  readonly name: string;
  readonly type: "int" | "number" | "bool" | "enum" | "string";
  readonly description: string | null;
  readonly value: ParameterValue;
  readonly default: ParameterValue;
  readonly min: number | null;
  readonly max: number | null;
  readonly values: readonly string[] | null;
  /** The layer that Locked it: the business cannot change it. */
  readonly lockedBy: string | null;
}

export interface Settings {
  readonly version: string;
  readonly parameters: readonly SettingsParameter[];
}

/** A proposed Tree Change and its preview. `suggested` waits for an Owner. */
export interface TreeChange {
  readonly id: string;
  readonly status: "proposed" | "suggested" | "applied";
  readonly origin: string;
  readonly proposedBy: string;
  readonly preview: {
    readonly baseVersion: string;
    readonly version: string;
    readonly lines: readonly string[];
    readonly headline: string;
    /** Open Runs affected, per Value Stream, or per Dormant Process run on its own (`process` instead). */
    readonly impact: readonly {
      readonly valueStream?: string;
      readonly process?: string;
      readonly name: string;
      readonly runs: readonly string[];
      readonly migratable: readonly string[];
    }[];
    /** The Tenant's recent Runs replayed on the proposed version, in plain words. */
    readonly replay?: {
      readonly testedOn: string;
      /** Who the Runs were replayed as. */
      readonly testedBy: string;
      readonly runs: number;
      readonly counts: Readonly<Record<string, number>>;
      readonly headline: string;
      readonly examples: readonly string[];
      /** Why the change cannot be confirmed; empty when it can. */
      readonly blocking: readonly string[];
      /** Runs the agent could re-run, and what that costs in thousandths of a Credit, until tested. */
      readonly agentTest?: { readonly runs: number; readonly calls: number; readonly milliCredits: number };
      /** Once tested with the agent: whether each re-run Run's new answer differs. */
      readonly agentTested?: readonly string[];
    };
  };
}

export interface AppliedTreeChange {
  readonly version: string;
  readonly previousVersion: string;
  readonly lines: readonly string[];
  /** Open Runs moved to the new version. */
  readonly migrated: readonly string[];
  /** Open Runs the Owner asked to move that stay on their version: their step is gone. */
  readonly pinned: readonly string[];
}

/** One person on the Users page. */
/** A new invitation as the Owner sees it: where an email one went, or a mobile one's code to pass on. */
export type SentInvitation =
  | { readonly kind: "email"; readonly sentTo: string; readonly expiresAt: string }
  | { readonly kind: "mobile"; readonly code: string; readonly expiresAt: string };

export interface PersonRow {
  readonly id: string;
  readonly name: string | null;
  readonly email: string | null;
  /** Their mobile number, when invited by one. */
  readonly mobile?: string | null;
  /** Role ids; `People.roles` names them. */
  readonly roles: readonly string[];
  readonly accountHolder: boolean;
  readonly status: "active" | "invited";
  readonly channels: readonly string[];
  readonly signInMethods: readonly string[];
  /** When they last signed in (ISO 8601), or null. */
  readonly lastActive: string | null;
}

export interface RoleName {
  readonly id: string;
  readonly name: string;
}

export interface People {
  readonly people: readonly PersonRow[];
  readonly roles: readonly RoleName[];
  /** Each Role no active person holds, and how many Work Items wait on it (on the Owners' Today until someone does). */
  readonly unheld?: readonly { readonly role: string; readonly waiting: number }[];
}

/** An External Assistant a person connected (Setup → Connections). */
export interface ConnectionRow {
  /** What revoking it takes. */
  readonly id: string;
  readonly person: { readonly id: string; readonly name: string | null };
  /** Its client_id. */
  readonly client: string;
  /** The name the client gave itself, as it gave it. */
  readonly name: string | null;
  /** Where it sends people back to, e.g. claude.ai. */
  readonly host: string;
  readonly registration: "cimd" | "dcr";
  /** `lamp.read`, and `lamp.act` when it may ask to do things. */
  readonly scope: string;
  readonly connectedAt: string;
  /** When it last called LAMP (ISO 8601), or null. */
  readonly lastUsed: string | null;
}

/** The most a Role may do on an area: approve includes act, act includes see. */
export type Access = "see" | "act" | "approve" | null;

export type SensitiveDataClass = "costs_and_margins" | "payroll" | "bank_and_cash" | "customer_pricing";

/** The Roles and permissions page. */
export interface Roles {
  readonly version: string;
  readonly roles: readonly (RoleName & { readonly description: string | null; readonly approves: readonly string[] })[];
  readonly areas: readonly {
    readonly node: string;
    readonly name: string;
    readonly access: Readonly<Record<string, Access>>;
  }[];
  readonly sensitiveData: readonly {
    readonly dataClass: SensitiveDataClass;
    readonly roles: Readonly<Record<string, boolean>>;
  }[];
}

/** One onboarding question (VS0), asked by the Task named in `step`. */
export interface OnboardingQuestion {
  readonly id: string;
  readonly step: string;
  readonly prompt: string;
  /**
   * `one`: pick one; `many`: pick any; `ranked`: pick 2–3 in order; `registration`: a GSTIN or none;
   * `particulars`: the address, its State and the signatory; `names`: any number of names, none included.
   */
  readonly kind: "one" | "many" | "ranked" | "registration" | "registration_details" | "particulars" | "names";
  readonly options: readonly { readonly id: string; readonly label: string }[];
  /** The Dimension whose values a `names` question asks for (#740). */
  readonly dimension?: string;
}

export interface ActivationSummary {
  readonly vertical: string;
  readonly businessProfile: Readonly<Record<string, string>>;
  readonly focusAreas: readonly { readonly id: string; readonly name: string }[];
  /** The Value Streams left off, which an Owner switches on later in chat (#508). */
  readonly off?: readonly { readonly id: string; readonly name: string }[];
  /** What the business's Tax Invoices will carry; absent without a GSTIN. */
  readonly supplierParticulars?: SupplierParticularsInput;
  /** The Dimension values activation adds, by Dimension (#740). */
  readonly dimensionValues?: readonly {
    readonly dimension: string;
    readonly label: string;
    readonly names: readonly string[];
  }[];
  readonly trialDays: number;
}

/** What the business's Tax Invoices carry about it as the supplier (#342). */
export interface SupplierParticularsInput {
  readonly legalName: string;
  readonly address: string;
  /** The address's GST State code: the GSTIN's first two digits. */
  readonly state: string;
  readonly gstin: string;
  readonly signatory: string;
  /** In paise. */
  readonly aggregateTurnover: number;
}

export interface SupplierParticulars extends SupplierParticularsInput {
  readonly stateName: string | null;
  /** The Owner who set them, or `system` for the one-time import. */
  readonly by: string;
  readonly at: string;
}

export interface Trial {
  readonly startsOn: string;
  /** The first day after the trial. */
  readonly endsOn: string;
}

export interface Onboarding {
  readonly status: "in_progress" | "waitlisted" | "activated";
  readonly answers: Readonly<Record<string, unknown>>;
  readonly question?: OnboardingQuestion;
  readonly summary?: ActivationSummary;
  readonly trial?: Trial;
}

/** An answer to the question asked now. */
export interface OnboardingAnswer {
  readonly question: string;
  /** The Dimension a `names` question asks about. */
  readonly dimension?: string;
  readonly value: unknown;
}

/** One kind of task's use of Credits this month: Run steps, or a task class's model calls. */
export interface CreditUsage {
  readonly kind: string;
  readonly label: string;
  readonly tier: string;
  readonly count: number;
  readonly milliCredits: number;
}

export interface CreditPack {
  readonly id: string;
  readonly credits: number;
  /** Before GST. */
  readonly pricePaise: number;
}

export interface CreditTopUp {
  readonly pack: string;
  readonly credits: number;
  readonly amountPaise: number;
  readonly automatic: boolean;
  readonly at: string;
}

/** The Ring the business is on (M3-20b): when new LAMP releases reach it. `changeable` is false in LAMP's own Ring. */
export interface RingSetting {
  readonly ring: "internal" | "alpha" | "general";
  readonly changeable: boolean;
}

/** One move of the business's LAMP release, with what changed for it; a move back is a rollback. */
export interface ReleaseChange {
  readonly release: string;
  readonly previous: string;
  readonly at: string;
  readonly rolledBack: boolean;
  readonly notes: readonly string[];
}

/** What changed in LAMP, as `GET /api/releases/changes` answers: newest first. Owners only. */
export interface WhatChanged {
  readonly ring: RingSetting["ring"];
  readonly release: string;
  readonly changes: readonly ReleaseChange[];
}

export type AutonomyLevel = "manual" | "ask" | "auto";

/** A ladder move: one rung, and the Tasks a Tree Change sets to it. */
export interface LadderMove {
  readonly to: AutonomyLevel;
  readonly tasks: readonly string[];
}

/** One kind of work LAMP does on the earned-autonomy ladder, as `GET /api/quality/autonomy` answers. */
export interface TaskClassLadder {
  readonly taskClass: string;
  /** What it does, in the Owner's words, e.g. "reading orders from messages". */
  readonly name: string;
  /** The highest level it has earned in LAMP's tests, and when. */
  readonly ceiling: { readonly level: AutonomyLevel; readonly evaluatedOn: string | null };
  readonly tasks: readonly {
    readonly task: string;
    readonly name: string;
    readonly valueStreams: readonly string[];
    /** The level its Work Items open at now. */
    readonly level: AutonomyLevel;
    readonly source: "tree" | "earned" | "track_record" | "kill_switch";
    /** The level the Business Tree sets. */
    readonly setTo: AutonomyLevel;
    readonly cappedBy: "not_earned" | "paused" | "track_record" | "set_by_you" | null;
  }[];
  /** This business's counts over the last `days` days. */
  readonly counts: {
    readonly drafted: number;
    readonly approvedUnchanged: number;
    readonly changed: number;
    readonly rejected: number;
    readonly correctedLater: number;
  };
  readonly lower: LadderMove | null;
  /** Never above the ceiling. */
  readonly raise: LadderMove | null;
  /** What this business's own track record did to it: the level it holds it to, a Suggestion waiting, the drops. */
  readonly trackRecord?: {
    readonly cap: { readonly level: AutonomyLevel; readonly reason: string; readonly since: string } | null;
    readonly suggestion: { readonly to: AutonomyLevel; readonly reason: string; readonly suggestedAt: string } | null;
    readonly notices: readonly { readonly level: AutonomyLevel; readonly reason: string; readonly at: string }[];
  };
}

export interface AutonomyLadder {
  readonly days: number;
  readonly taskClasses: readonly TaskClassLadder[];
}

/** This installation's licence, as `GET /api/licence` answers (ADR-0008): the Cockpit's banner for Owners. */
export interface LicenceBanner {
  readonly state: "not_required" | "valid" | "grace" | "lapsed";
  /** Whole days left before the business becomes read-only, while the licence is in grace. */
  readonly daysLeft?: number;
  /** For Owners: the licence in grace or lapsed, and stale backups, in plain words. Empty for anyone else. */
  readonly notices: readonly string[];
}

/**
 * Whether LAMP's agents are paused (a Kill Switch), as `GET /api/kill-switch` answers. While `stopped`, Runs wait at
 * their next step and outbound messages and filings are held; people keep read access.
 */
export interface AgentsStatus {
  readonly stopped: {
    readonly scope: { readonly kind: "tenant" | "cell" | "global" };
    readonly reason: string;
    /** An Owner of the business, or LAMP operations. */
    readonly byKind: "owner" | "lamp_ops";
    readonly by: string;
    readonly since: string;
  } | null;
  /** What everyone in the business is told while LAMP's agents are paused. */
  readonly notice: string | null;
  /** Task classes whose agent Tasks wait for a person to confirm. */
  readonly askOnly: readonly string[];
  /** Outbound messages and filings held until LAMP's agents are resumed. */
  readonly held: number;
}

/** The Tenant's Credits this month (the Model Budget), as `GET /api/credits` answers. Amounts in milli-Credits. */
export interface Credits {
  readonly month: string;
  /** Null on no Plan, which includes what is left of the trial's grant. */
  readonly plan: string | null;
  readonly includedMilliCredits: number;
  readonly boughtMilliCredits: number;
  readonly budgetMilliCredits: number;
  readonly usedMilliCredits: number;
  readonly remainingMilliCredits: number;
  readonly usedPercent: number;
  /** Nothing left: Runs pause at their next step until a top-up. */
  readonly empty: boolean;
  /** The pack auto-recharge buys; null: off. */
  readonly autoRecharge: string | null;
  readonly byKind: readonly CreditUsage[];
  readonly topUps: readonly CreditTopUp[];
  readonly packs: readonly CreditPack[];
  /** Today's requests LAMP's model gave no answer for: the free ones are LAMP's cost, the rest took Credits (#929). */
  readonly noCompletionToday: {
    readonly free: number;
    readonly charged: number;
    readonly chargedMilliCredits: number;
    /** How many a day are free. */
    readonly freePerDay: number;
  };
}

/** A price, its GST and the total collected, in paise. */
export interface Quote {
  readonly pricePaise: number;
  readonly gstPaise: number;
  readonly totalPaise: number;
}

export type PlanId = "starter" | "growth" | "pro";

export type TrialStanding =
  | { readonly status: "none" }
  | (Trial & { readonly status: "running"; readonly daysLeft: number })
  | (Trial & { readonly status: "converted"; readonly convertedOn: string })
  | (Trial & { readonly status: "ended"; readonly exportUntil: string });
export type BillingPeriod = "monthly" | "annual";

/** The Tenant's Plan and billing (#212), as `GET /api/billing` answers. Money in paise; Credits in milli-Credits. */
export interface BillingPage {
  /** How the Cell takes payments: Razorpay, the development mock, or not at all. */
  readonly provider: "razorpay" | "mock" | "none";
  /** Whether the person reading is the Account Holder, who alone subscribes and cancels. */
  readonly accountHolder: boolean;
  readonly plan: {
    readonly id: PlanId;
    readonly name: string;
    readonly billingPeriod: BillingPeriod;
    readonly periodStart: string;
    readonly periodEnd: string;
  } | null;
  readonly status: "pending" | "active" | "charge_failed" | "halted" | "cancelled" | "paid" | null;
  /** Where the trial stands (#211): running with whole days left, converted to a Plan, ended unpaid, or none. */
  readonly trial: TrialStanding;
  readonly nextChargeOn: string | null;
  readonly overdueSince: string | null;
  readonly readOnly: readonly string[];
  readonly people: { readonly month: string; readonly active: number; readonly included: number };
  readonly registrations: { readonly count: number; readonly included: number };
  readonly credits: {
    readonly month: string;
    readonly includedMilliCredits: number;
    readonly boughtMilliCredits: number;
    readonly usedMilliCredits: number;
  };
  /** Payments and failed charges, newest first. */
  readonly payments: readonly {
    readonly kind: string;
    readonly at: string;
    readonly amountPaise: number | null;
    readonly reference: string;
    readonly what: string;
  }[];
  readonly addOns: readonly {
    readonly month: string;
    readonly activePeople: number;
    readonly includedPeople: number;
    readonly quantity: number;
    readonly unitPricePaise: number;
  }[];
  readonly offers: {
    readonly plans: readonly {
      readonly id: PlanId;
      readonly name: string;
      readonly includedPeople: number;
      readonly includedRegistrations: number;
      readonly includedCredits: number;
      readonly monthly: Quote;
      readonly annual: Quote;
    }[];
    readonly packs: readonly { readonly id: string; readonly credits: number; readonly quote: Quote }[];
    readonly extraPerson: Quote;
  };
  /** Why subscribing to a Plan is refused now; null when it may be done. */
  readonly planCheckoutRefused: string | null;
  readonly cancellable: boolean;
  /** LAMP's GST tax invoices for the business's payments, newest first. */
  readonly invoices: readonly LampInvoice[];
  /** What LAMP's invoices carry about the business, and the Registrations the Account Holder may bill instead. */
  readonly invoiceParticulars: {
    readonly current: InvoiceParticulars | null;
    readonly chosen: boolean;
    readonly registrations: readonly {
      readonly gstin: string;
      readonly legalName: string;
      readonly address: string | null;
      /** The GSTIN is mistyped: it is corrected in Settings, and cannot be chosen until then. */
      readonly needsCorrecting: boolean;
    }[];
  };
}

/** One of LAMP's GST tax invoices to the business: its PDF opens from `pdf`. */
export interface LampInvoice {
  readonly id: string;
  readonly number: string;
  readonly date: string;
  readonly totalPaise: number;
  readonly paidPaise: number;
  readonly pdf: string;
}

/** What LAMP's invoices carry about the business (ToS 6.2): the GSTIN billed, or none. */
export interface InvoiceParticulars {
  readonly legalName: string;
  readonly address: string;
  readonly state: string;
  readonly gstin: string | null;
}

/** A checkout made: Razorpay's page to pay on, or null when it is paid already. */
export interface BillingCheckout {
  readonly checkout: string;
  readonly url: string | null;
  readonly paid: boolean;
  readonly quote: Quote;
}

/** Who saved a Note, when and where (ADR-0021). */
export interface NoteSource {
  readonly by: string;
  readonly agent?: string;
  readonly at: string;
  readonly channel: string;
  readonly reference?: string;
}

export interface MemoryNote {
  readonly id: string;
  readonly text: string;
  readonly source: NoteSource;
}

export const RELATIONSHIP_NOTE_CATEGORIES = {
  family_event: "Family event",
  health: "Health (kept 90 days)",
  festival_greeting: "Festival greeting",
  greeting_preference: "Greeting preference",
  courtesy: "Other courtesy",
} as const;
export type RelationshipNoteCategory = keyof typeof RELATIONSHIP_NOTE_CATEGORIES;

export interface RelationshipNote extends MemoryNote {
  readonly contact: string;
  readonly category: RelationshipNoteCategory;
  /** Health notes only: the last day it is kept unless renewed. */
  readonly expiresOn: string | null;
}

/** A Counterparty's Memory panel, as `GET /api/memory/counterparties/:id` answers. */
export interface CounterpartyMemory {
  readonly counterparty: { readonly id: string; readonly name: string };
  /** Calculated from the Ledgers, never kept; null before anything is owed. */
  readonly behaviour: {
    readonly paid: number;
    readonly onTimePercent: number | null;
    readonly averageDaysLate: number;
    readonly overdue: number;
  } | null;
  readonly notes: readonly MemoryNote[];
  /** Absent when the person's Roles may not see them (Owners see them by default). */
  readonly relationshipNotes?: readonly RelationshipNote[];
}

/** A pattern LAMP noticed, which the Owner accepts or dismisses. */
export interface Suggestion {
  readonly key: string;
  readonly title: string;
  readonly why: string;
  /** Accepting it proposes a Tree Change, or opens an action. */
  readonly proposal: "tree_change" | "action";
}

/** The business's Memory, as `GET /api/memory` answers: this week's Suggestions and its Counterparties. */
export interface BusinessMemory {
  readonly suggestions: readonly Suggestion[];
  readonly counterparties: readonly { readonly id: string; readonly name: string }[];
}

/** What accepting a Suggestion did: a Tree Change for an Owner to confirm, or an action to take. */
export type AcceptedSuggestion = { readonly change: TreeChange } | { readonly action: string };

export interface Api {
  /** Settings → Privacy. */
  readonly privacy: PrivacyApi;
  /** AI models, Security, Help and System status, under Setup (#488). */
  readonly models: ModelsApi;
  readonly security: SecurityApi;
  readonly help: HelpApi;
  readonly status: StatusApi;
  /** Settings → Dimension values (V-10); without it, Settings has no such tab. */
  readonly dimensionValues?: DimensionValuesApi;
  /** Settings → Expense accounts (V-10); without it, Settings has no such tab. */
  readonly expenseAccounts?: ExpenseAccountsApi;
  /** Settings → Ledger Mapping (V-12); without it, Settings has no such tab. */
  readonly ledgerMapping?: LedgerMappingApi;
  /** The Books Link by file (V-13), on the Documents page. */
  readonly booksLink?: BooksLinkApi;
  /** Books Reconciliation (V-18), on the Documents page. */
  readonly booksReconciliation?: BooksReconciliationApi;
  /** Confirming it is the signed-in person just before a sensitive action. */
  readonly stepUp: StepUpApi;
  session(): Promise<Session | undefined>;
  signOut(): Promise<void>;
  /** The signed-in person's own profile. */
  me(): Promise<Me>;
  /**
   * The Cockpit pages the person's Roles open (#513), by their ids: `opens`, the pages they may open, and `listed`, those
   * the sidebar lists, which leaves out a page with nothing to show yet (#989).
   */
  pages?(): Promise<{ readonly listed: readonly string[]; readonly opens: readonly string[] }>;
  /** Keeps the language on the person's profile. */
  setLanguage(language: Language): Promise<void>;
  thread(): Promise<readonly ChatEntry[]>;
  send(text: string): Promise<readonly ChatEntry[]>;
  /** Hands LAMP a file in chat, with the person's words if any. */
  sendFile(file: FileToSend, text?: string): Promise<readonly ChatEntry[]>;
  /** Confirms the kind LAMP proposed for a file (`yes`), says what it is, or `none`. */
  chooseFileKind(file: string, kind: "yes" | "none" | FileKind): Promise<readonly ChatEntry[]>;
  retryFile(file: string): Promise<readonly ChatEntry[]>;
  /** Records a previewed drawing from a file as a draft Drawing Revision, as read or as the person corrected it. */
  recordFile(file: string, corrected?: DrawingCorrections): Promise<readonly ChatEntry[]>;
  /** Starts developing the part to the drawing recorded from a file. */
  developFile(file: string): Promise<readonly ChatEntry[]>;
  /** Keeps a file LAMP does not read against a record the person names, with their own words. */
  keepFile(
    file: string,
    keep: { readonly object: KeptAgainst; readonly name: string; readonly words: string },
  ): Promise<readonly ChatEntry[]>;
  /** Sends a held gate entry back, with the person's reason. */
  sendBackFile(file: string, reason: string): Promise<readonly ChatEntry[]>;
  /** `text`: a message's words as the person confirmed them in its preview; `form`: the card's Form as filled in. */
  act(
    card: Pick<Card, "run" | "workItem">,
    action: CardAction,
    input?: {
      readonly reason?: string | undefined;
      readonly text?: string | undefined;
      readonly form?: CardForm | undefined;
    },
  ): Promise<ActReply>;
  /** What the person chose to be emailed about their work (#512). */
  notices(): Promise<StaffNoticeView>;
  /** Keeps what the person chose to be emailed about their work, and where: a new address is emailed a code. */
  chooseNotices(choice: StaffNoticeChoice): Promise<StaffNoticeView>;
  /** Confirms the address with the code emailed to it (#655). */
  confirmNotices(code: string): Promise<StaffNoticeView>;
  /** Undoes the email to a customer a confirmed card sends, while it waits to go: false once it has gone (#512). */
  undoEmail(card: Pick<Card, "run" | "workItem">): Promise<boolean>;
  today(): Promise<Today>;
  /** The Floor: a card for each switched-on Value Stream with its live Runs. */
  floor(): Promise<readonly FloorCard[]>;
  /** Insights: each Value Driver sub-driver's KPIs over the last months, and what moved them. */
  insights(): Promise<Insights>;
  /** One page of the Runs the person may see, newest first. */
  runs(filters: { show?: RunShow; q?: string; page?: number }): Promise<ListPage<RunRow>>;
  /** One page of issued Documents, newest first. */
  documents(filters: { q?: string; from?: string; to?: string; page?: number }): Promise<ListPage<DocumentRow>>;
  /** One page of Counterparties, by name. */
  counterparties(filters: { kind?: CounterpartyKind; q?: string; page?: number }): Promise<ListPage<CounterpartyRow>>;
  /** One page of Items with the stock on hand, by name. */
  items(filters: { stock?: StockShow; q?: string; page?: number }): Promise<ListPage<ItemRow>>;
  /**
   * Adds a customer, supplier or job worker, or corrects the one `id` names; the same checks and Roles as chat. A
   * correction's null clears that field on record.
   */
  saveCounterparty(
    kind: CounterpartyKind,
    fields: MasterDataCorrections & { readonly name: string },
    id?: string,
  ): Promise<MasterDataSaved>;
  /** A supplier's PAN (masked unless the person holds ID numbers), usual Nature of Payment and certificate. */
  counterpartyTaxParticulars(counterparty: string): Promise<TaxParticulars>;
  /** Adds an Item, or corrects the one `id` names, with its opening stock once; a correction's null clears a field. */
  saveItem(fields: MasterDataCorrections & { readonly name: string }, id?: string): Promise<MasterDataSaved>;
  /** Confirms the draft a message shows in the thread, with the person's corrections. */
  confirmMasterData(message: string, corrected?: MasterDataCorrections): Promise<readonly ChatEntry[]>;
  /** A part's BOM and routing in force, as its Item's page shows them, and whether the person may give them. */
  itemBom(item: string): Promise<ItemBomRead>;
  /** Gives or corrects a part's BOM and routing from its Item's page: what LAMP did, in words. */
  giveItemBom(item: string, given: PartBomGiven): Promise<MasterDataSaved>;
  /**
   * The files kept against a record (#575), in the order they were kept; refused when the person may not read it.
   * Without it, a record's page lists no files.
   */
  keptFiles?(object: KeptAgainst, record: string): Promise<readonly KeptFileRow[]>;
  /** Confirms the BOM a message drafted in the thread, as drafted or as corrected. */
  confirmBom(message: string, given?: PartBomGiven): Promise<readonly ChatEntry[]>;
  /** The top bar's search: orders, Counterparties and Documents of the business. */
  search(q: string): Promise<readonly SearchResult[]>;
  settings(): Promise<Settings>;
  /** The business's Supplier Particulars, Owner only; null until set. */
  supplierParticulars(): Promise<SupplierParticulars | null>;
  /** Changes them, Owner only: the GSTIN must be of the address's State. Answers what is in force after it. */
  setSupplierParticulars(input: SupplierParticularsInput): Promise<SupplierParticulars | null>;
  /** Proposes setting one Parameter: a Tree Change, validated and previewed, kept for an Owner to confirm. */
  proposeSetting(node: string, name: string, value: ParameterValue): Promise<TreeChange>;
  pendingChanges(): Promise<readonly TreeChange[]>;
  /** The earned-autonomy ladder, for the Quality page. */
  autonomyLadder(): Promise<AutonomyLadder>;
  /** Proposes a ladder move: a Tree Change setting the Tasks' Autonomy Level, previewed for an Owner to confirm. */
  proposeAutonomy(move: LadderMove): Promise<TreeChange>;
  /**
   * Accepts the track record's Suggestion to raise a kind of work back up; with `ladder`, the ladder move up to it is
   * proposed too, previewed for the Owner to confirm.
   */
  acceptRaiseBack(
    taskClass: string,
    ladder: boolean,
  ): Promise<{ readonly to: AutonomyLevel; readonly change: TreeChange | null }>;
  /** Dismisses it: not suggested again for 30 days. */
  dismissRaiseBack(taskClass: string): Promise<void>;
  /** Confirms a Tree Change; with `migrate`, open Runs whose step still exists move to the new version. */
  confirmChange(id: string, migrate: boolean): Promise<AppliedTreeChange>;
  /** Tests a Tree Change with the agent at the Credits its preview shows; answers the change with what it found. */
  testChangeWithAgent(id: string, milliCredits: number): Promise<TreeChange>;
  /** Everyone in the business and the Tenant's Roles. Owners only. */
  people(): Promise<People>;
  /**
   * Invites a person by email or mobile number with one or more Roles. An email invitation goes to the email; a
   * mobile one's code comes back, for the Owner to pass on.
   */
  invite(input: { email?: string; mobile?: string; name?: string; roles: readonly string[] }): Promise<SentInvitation>;
  /**
   * Sends someone still invited a new invitation, which replaces the one before. An email one goes to their email;
   * a mobile one's code comes back, for the Owner to pass on: the person sends `/start <code>` to LAMP's Telegram bot.
   */
  reinvite(personId: string): Promise<SentInvitation>;
  /** Replaces a person's Roles; answers their Roles now. */
  setRoles(personId: string, roles: readonly string[]): Promise<readonly string[]>;
  /** The External Assistants connected: everyone's for an Owner, otherwise the person's own. */
  connections(): Promise<readonly ConnectionRow[]>;
  /** Revokes a connection: the assistant's next call is refused. */
  revokeConnection(id: string): Promise<void>;
  /** What each Role may see, do and approve, and the data it sees. Owners only. */
  roles(): Promise<Roles>;
  /** Proposes which Sensitive Data Classes a Role sees: a Tree Change, previewed, kept for an Owner to confirm. */
  proposeRoleData(role: string, sensitiveData: readonly SensitiveDataClass[]): Promise<TreeChange>;
  /** Where onboarding stands: the question asked now, or the activation summary. Owners only. */
  onboarding(): Promise<Onboarding>;
  /** Keeps one onboarding answer; answers where onboarding stands after it. */
  answerOnboarding(answer: OnboardingAnswer): Promise<Onboarding>;
  /** Confirms the activation summary: the Business Profile and Focus Areas apply, and the trial starts. */
  activate(): Promise<{ readonly version: string; readonly summary: ActivationSummary; readonly trial: Trial }>;
  /** This month's Credits by kind of task, the packs on sale and auto-recharge. Owners only. */
  credits(): Promise<Credits>;
  /** Buys a Credit pack; `key` makes a repeated request buy it once. Answers the Credits after it. */
  topUp(pack: string, key: string): Promise<Credits>;
  /** Turns auto-recharge on with `pack`, or off with null. */
  setAutoRecharge(pack: string | null): Promise<Credits>;
  /** The Plan, period, people, Registrations, Credits and payments, and what can be bought. Owners only. */
  billing(): Promise<BillingPage>;
  /** Subscribes to a Plan for the total, GST included, the Account Holder confirmed. Account Holder only. */
  subscribe(plan: PlanId, billingPeriod: BillingPeriod, confirmedAmountPaise: number): Promise<BillingCheckout>;
  /** Buys a Credit pack for the total, GST included, the Owner confirmed. */
  buyCreditPack(pack: string, confirmedAmountPaise: number): Promise<BillingCheckout>;
  /** Cancels the Plan; it runs until the date answered. Account Holder only. */
  cancelSubscription(): Promise<{ readonly until: string | null }>;
  /** The Account Holder chooses what LAMP's invoices carry: a Registration, or none with the State. */
  chooseInvoiceParticulars(choice: {
    readonly gstin: string | null;
    readonly legalName: string;
    readonly address: string;
    readonly state?: string;
  }): Promise<InvoiceParticulars>;
  /** Whether LAMP's agents are paused in the business, and why. Anyone signed in. */
  agents(): Promise<AgentsStatus>;
  /** This installation's licence and the Owners' notices about it and its backups. */
  licence(): Promise<LicenceBanner>;
  /** The Ring the business is on: whether new LAMP releases reach it early. Owners only. */
  ring(): Promise<RingSetting>;
  /** Opts the business in to the alpha Ring, or out of it. Owners only. */
  setAlpha(alpha: boolean): Promise<RingSetting>;
  /** Each move of the business's LAMP release, with what changed for it. Owners only. */
  releaseChanges(): Promise<WhatChanged>;
  /** Pulls the business's Kill Switch: LAMP's agents stop at once. Owners only. */
  pauseAgents(reason: string): Promise<AgentsStatus>;
  /** Releases the business's Kill Switch an Owner pulled; what was held is sent. Owners only. */
  resumeAgents(): Promise<AgentsStatus>;
  /** This week's Suggestions (at most five) and the Counterparties with a Memory panel. */
  memory(): Promise<BusinessMemory>;
  /** What LAMP knows about a Counterparty: calculated behaviour, Notes and Relationship Notes. */
  counterpartyMemory(counterparty: string): Promise<CounterpartyMemory>;
  addNote(counterparty: string, input: { text: string; channel: string }): Promise<CounterpartyMemory>;
  /** Only a person writes a Relationship Note; excluded categories are refused. */
  addRelationshipNote(
    counterparty: string,
    input: { contact: string; category: RelationshipNoteCategory; text: string },
  ): Promise<CounterpartyMemory>;
  /** Keeps a health note another 90 days. */
  renewNote(counterparty: string, note: string): Promise<CounterpartyMemory>;
  /** "Forget that": deletes the note; the Audit Log keeps that it was forgotten, never its words. */
  forgetNote(counterparty: string, note: string): Promise<CounterpartyMemory>;
  acceptSuggestion(key: string): Promise<AcceptedSuggestion>;
  /** Dismissed Suggestions are not repeated for 30 days. */
  dismissSuggestion(key: string): Promise<void>;
  /** Every Audit Log entry on a Run, oldest first. Needs a Role that sees every node. */
  runEntries(run: string): Promise<readonly AuditEntry[]>;
  /** The Credits a Run has used, per step and Task. Needs a Role that sees every node. */
  runCredits(run: string): Promise<RunCredits>;
  /**
   * A Run's own page (#513), for anyone who may see the Run: what it is about, why it stands where it does, its steps,
   * whether the person reads its Audit Log entries and Credits, and its order's cost where they may see it.
   */
  runPage(run: string): Promise<RunPage>;
  /** One page of Audit Log entries matching `filters`, oldest first, after `filters.after`. */
  auditPage(filters: AuditFilters, limit?: number): Promise<readonly AuditEntry[]>;
}

/** The most entries `GET /audit` answers at once. */
const AUDIT_PAGE = 200;

/** `path` with the filters given as its query string; empty and absent ones are left out. */
function withFilters(path: string, filters: Readonly<Record<string, string | number | undefined>>): string {
  const query = new URLSearchParams();
  for (const [name, value] of Object.entries(filters))
    if (value !== undefined && value !== "") query.set(name, String(value));
  return query.size > 0 ? `${path}?${query.toString()}` : path;
}

/** The Setup pages' own APIs (#488): AI models, Security, Help and System status. */
export const setupApis = (fetcher: Fetch): Pick<Api, "models" | "security" | "help" | "status"> => ({
  models: createModelsApi(fetcher),
  security: createSecurityApi(fetcher),
  help: createHelpApi(fetcher),
  status: createStatusApi(fetcher),
});

export function createApi(fetcher: Fetch = (input, init) => fetch(input, init)): Api {
  return {
    privacy: createPrivacyApi(fetcher),
    ...setupApis(fetcher),
    stepUp: createStepUpApi(fetcher),
    dimensionValues: createDimensionValuesApi(fetcher),
    expenseAccounts: createExpenseAccountsApi(fetcher),
    ledgerMapping: createLedgerMappingApi(fetcher),
    booksLink: createBooksLinkApi(fetcher),
    booksReconciliation: createBooksReconciliationApi(fetcher),
    async session() {
      try {
        return await call<Session>(fetcher, "/auth/session");
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return undefined;
        throw error;
      }
    },
    async signOut() {
      await call(fetcher, "/auth/sign-out", { method: "POST" });
    },
    me: () => call<Me>(fetcher, "/api/me"),
    pages: async () => {
      const { pages, opens } = await call<{ pages: string[]; opens: string[] }>(fetcher, "/api/pages");
      return { listed: pages, opens };
    },
    async setLanguage(language) {
      await call(fetcher, "/api/me/language", { method: "PUT", json: { language } });
    },
    thread: async () => (await call<{ entries: ChatEntry[] }>(fetcher, "/api/chat")).entries,
    send: async (text) =>
      (await call<{ entries: ChatEntry[] }>(fetcher, "/api/chat/messages", { json: { text } })).entries,
    sendFile: async (file, text) =>
      (
        await call<{ entries: ChatEntry[] }>(fetcher, "/api/chat/files", {
          json: { ...file, ...(text ? { text } : {}) },
        })
      ).entries,
    chooseFileKind: async (file, kind) =>
      (
        await call<{ entries: ChatEntry[] }>(fetcher, `/api/chat/files/${encodeURIComponent(file)}/kind`, {
          json: { kind },
        })
      ).entries,
    retryFile: async (file) =>
      (await call<{ entries: ChatEntry[] }>(fetcher, `/api/chat/files/${encodeURIComponent(file)}/retry`, { json: {} }))
        .entries,
    recordFile: async (file, corrected) =>
      (
        await call<{ entries: ChatEntry[] }>(fetcher, `/api/chat/files/${encodeURIComponent(file)}/record`, {
          json: { ...corrected },
        })
      ).entries,
    developFile: async (file) =>
      (
        await call<{ entries: ChatEntry[] }>(fetcher, `/api/chat/files/${encodeURIComponent(file)}/develop`, {
          json: {},
        })
      ).entries,
    keepFile: async (file, keep) =>
      (
        await call<{ entries: ChatEntry[] }>(fetcher, `/api/chat/files/${encodeURIComponent(file)}/keep`, {
          json: { ...keep },
        })
      ).entries,
    sendBackFile: async (file, reason) =>
      (
        await call<{ entries: ChatEntry[] }>(fetcher, `/api/chat/files/${encodeURIComponent(file)}/send-back`, {
          json: { reason },
        })
      ).entries,
    act: async (card, action, input) => {
      const { entries, emailQueued } = await call<{ entries: ChatEntry[]; emailQueued?: boolean }>(
        fetcher,
        `/api/runs/${card.run}/work-items/${card.workItem}/${action}`,
        {
          json: {
            ...(input?.reason ? { reason: input.reason } : {}),
            ...(input?.text === undefined ? {} : { text: input.text }),
            ...(input?.form === undefined ? {} : { form: input.form }),
          },
        },
      );
      return Object.assign(entries, { emailQueued: emailQueued === true });
    },
    notices: () => call<StaffNoticeView>(fetcher, "/api/notices"),
    chooseNotices: (choice) => call<StaffNoticeView>(fetcher, "/api/notices", { json: choice }),
    confirmNotices: (code) => call<StaffNoticeView>(fetcher, "/api/notices/confirm", { json: { code } }),
    undoEmail: async (card) =>
      (
        await call<{ undone: boolean }>(fetcher, `/api/runs/${card.run}/work-items/${card.workItem}/email/undo`, {
          json: {},
        })
      ).undone,
    today: () => call<Today>(fetcher, "/api/today"),
    floor: async () => (await call<{ cards: FloorCard[] }>(fetcher, "/api/floor")).cards,
    insights: () => call<Insights>(fetcher, "/api/insights"),
    runs: (filters) => call<ListPage<RunRow>>(fetcher, withFilters("/api/runs", filters)),
    documents: (filters) => call<ListPage<DocumentRow>>(fetcher, withFilters("/api/documents", filters)),
    counterparties: (filters) => call<ListPage<CounterpartyRow>>(fetcher, withFilters("/api/counterparties", filters)),
    items: (filters) => call<ListPage<ItemRow>>(fetcher, withFilters("/api/items", filters)),
    saveCounterparty: (kind, fields, id) =>
      call<MasterDataSaved>(fetcher, id ? `/api/counterparties/${encodeURIComponent(id)}` : "/api/counterparties", {
        json: { kind, ...fields },
      }),
    counterpartyTaxParticulars: (counterparty) =>
      call<TaxParticulars>(fetcher, `/api/counterparties/${encodeURIComponent(counterparty)}/tax-particulars`),
    saveItem: (fields, id) =>
      call<MasterDataSaved>(fetcher, id ? `/api/items/${encodeURIComponent(id)}` : "/api/items", { json: fields }),
    confirmMasterData: async (message, corrected) =>
      (
        await call<{ entries: ChatEntry[] }>(fetcher, `/api/chat/master-data/${encodeURIComponent(message)}`, {
          json: { ...corrected },
        })
      ).entries,
    itemBom: (item) => call<ItemBomRead>(fetcher, `/api/items/${encodeURIComponent(item)}/bom`),
    giveItemBom: (item, given) =>
      call<MasterDataSaved>(fetcher, `/api/items/${encodeURIComponent(item)}/bom`, { json: given }),
    keptFiles: async (object, record) =>
      (
        await call<{ files: KeptFileRow[] }>(
          fetcher,
          `/api/kept-files/${encodeURIComponent(object)}/${encodeURIComponent(record)}`,
        )
      ).files,
    confirmBom: async (message, given) =>
      (
        await call<{ entries: ChatEntry[] }>(fetcher, `/api/chat/bom/${encodeURIComponent(message)}`, {
          json: { ...given },
        })
      ).entries,
    search: async (q) => (await call<{ results: SearchResult[] }>(fetcher, withFilters("/api/search", { q }))).results,
    settings: () => call<Settings>(fetcher, "/api/settings"),
    proposeSetting: async (node, name, value) =>
      (
        await call<{ change: TreeChange }>(fetcher, "/api/settings/changes", {
          json: { edits: { nodes: { [node]: { params: { [name]: value } } } } },
        })
      ).change,
    autonomyLadder: () => call<AutonomyLadder>(fetcher, "/api/quality/autonomy"),
    acceptRaiseBack: (taskClass, ladder) =>
      call<{ to: AutonomyLevel; change: TreeChange | null }>(
        fetcher,
        `/api/quality/autonomy/${encodeURIComponent(taskClass)}/accept`,
        { json: { ladder } },
      ),
    async dismissRaiseBack(taskClass) {
      await call(fetcher, `/api/quality/autonomy/${encodeURIComponent(taskClass)}/dismiss`, { json: {} });
    },
    proposeAutonomy: async ({ to, tasks }) =>
      (
        await call<{ change: TreeChange }>(fetcher, "/api/settings/changes", {
          json: { edits: { nodes: Object.fromEntries(tasks.map((task) => [task, { autonomy: to }])) } },
        })
      ).change,
    people: () => call<People>(fetcher, "/api/people"),
    invite: async (input) =>
      (await call<{ invitation: SentInvitation }>(fetcher, "/api/people", { json: input })).invitation,
    reinvite: async (personId) =>
      (
        await call<{ invitation: SentInvitation }>(fetcher, `/api/people/${encodeURIComponent(personId)}/invitation`, {
          json: {},
        })
      ).invitation,
    setRoles: async (personId, roles) =>
      (
        await call<{ roles: string[] }>(fetcher, `/api/people/${encodeURIComponent(personId)}/roles`, {
          json: { roles },
        })
      ).roles,
    connections: async () => (await call<{ connections: ConnectionRow[] }>(fetcher, "/api/connections")).connections,
    async revokeConnection(id) {
      await call(fetcher, `/api/connections/${encodeURIComponent(id)}/revoke`, { json: {} });
    },
    roles: () => call<Roles>(fetcher, "/api/roles"),
    proposeRoleData: async (role, sensitiveData) =>
      (
        await call<{ change: TreeChange }>(fetcher, "/api/settings/changes", {
          json: { edits: { roles: { [role]: { sensitive_data: sensitiveData } } } },
        })
      ).change,
    credits: () => call<Credits>(fetcher, "/api/credits"),
    topUp: (pack, key) => call<Credits>(fetcher, "/api/credits/top-ups", { json: { pack, key } }),
    setAutoRecharge: (pack) => call<Credits>(fetcher, "/api/credits/auto-recharge", { json: { pack } }),
    billing: () => call<BillingPage>(fetcher, "/api/billing"),
    subscribe: (plan, billingPeriod, confirmedAmountPaise) =>
      call<BillingCheckout>(fetcher, "/api/billing/subscription", {
        json: { plan, billingPeriod, confirmedAmountPaise },
      }),
    buyCreditPack: (pack, confirmedAmountPaise) =>
      call<BillingCheckout>(fetcher, "/api/billing/credit-packs", { json: { pack, confirmedAmountPaise } }),
    cancelSubscription: () => call<{ until: string | null }>(fetcher, "/api/billing/subscription/cancel", { json: {} }),
    chooseInvoiceParticulars: (choice) =>
      call<InvoiceParticulars>(fetcher, "/api/billing/invoice-particulars", { json: choice }),
    agents: () => call<AgentsStatus>(fetcher, "/api/kill-switch"),
    licence: () => call<LicenceBanner>(fetcher, "/api/licence"),
    ring: () => call<RingSetting>(fetcher, "/api/settings/ring"),
    setAlpha: (alpha) => call<RingSetting>(fetcher, "/api/settings/ring", { method: "PUT", json: { alpha } }),
    supplierParticulars: async () =>
      (await call<{ current: SupplierParticulars | null }>(fetcher, "/api/settings/supplier-particulars")).current,
    setSupplierParticulars: async (input) =>
      (
        await call<{ current: SupplierParticulars | null }>(fetcher, "/api/settings/supplier-particulars", {
          method: "PUT",
          json: input,
        })
      ).current,
    releaseChanges: () => call<WhatChanged>(fetcher, "/api/releases/changes"),
    pauseAgents: (reason) => call<AgentsStatus>(fetcher, "/api/kill-switch", { json: { reason } }),
    resumeAgents: () => call<AgentsStatus>(fetcher, "/api/kill-switch/release", { json: {} }),
    memory: () => call<BusinessMemory>(fetcher, "/api/memory"),
    // Each path written out whole: tests/api-routes/ checks every one the web app calls is one the api serves.
    counterpartyMemory: (counterparty) =>
      call<CounterpartyMemory>(fetcher, `/api/memory/counterparties/${encodeURIComponent(counterparty)}`),
    addNote: (counterparty, input) =>
      call<CounterpartyMemory>(fetcher, `/api/memory/counterparties/${encodeURIComponent(counterparty)}/notes`, {
        json: input,
      }),
    addRelationshipNote: (counterparty, input) =>
      call<CounterpartyMemory>(
        fetcher,
        `/api/memory/counterparties/${encodeURIComponent(counterparty)}/relationship-notes`,
        { json: input },
      ),
    renewNote: (counterparty, note) =>
      call<CounterpartyMemory>(
        fetcher,
        `/api/memory/counterparties/${encodeURIComponent(counterparty)}/notes/${encodeURIComponent(note)}/renew`,
        { json: {} },
      ),
    forgetNote: (counterparty, note) =>
      call<CounterpartyMemory>(
        fetcher,
        `/api/memory/counterparties/${encodeURIComponent(counterparty)}/notes/${encodeURIComponent(note)}`,
        { method: "DELETE", json: {} },
      ),
    acceptSuggestion: (key) =>
      call<AcceptedSuggestion>(fetcher, `/api/suggestions/${encodeURIComponent(key)}/accept`, { json: {} }),
    async dismissSuggestion(key) {
      await call(fetcher, `/api/suggestions/${encodeURIComponent(key)}/dismiss`, { json: {} });
    },
    async runEntries(run) {
      const entries: AuditEntry[] = [];
      for (;;) {
        const query = new URLSearchParams({ run, limit: String(AUDIT_PAGE) });
        const last = entries.at(-1);
        if (last) query.set("after", String(last.sequence));
        const page = (await call<{ entries: AuditEntry[] }>(fetcher, `/audit?${query.toString()}`)).entries;
        entries.push(...page);
        if (page.length < AUDIT_PAGE) return entries;
      }
    },
    runCredits: (run) => call<RunCredits>(fetcher, `/runs/${encodeURIComponent(run)}/credits`),
    runPage: (run) => call<RunPage>(fetcher, `/api/runs/${encodeURIComponent(run)}`),
    auditPage: async (filters, limit = AUDIT_PAGE) =>
      (await call<{ entries: AuditEntry[] }>(fetcher, `/audit?${auditQuery(filters, limit).toString()}`)).entries,
    pendingChanges: async () => (await call<{ changes: TreeChange[] }>(fetcher, "/api/tree-changes")).changes,
    onboarding: () => call<Onboarding>(fetcher, "/api/onboarding"),
    answerOnboarding: (answer) => call<Onboarding>(fetcher, "/api/onboarding/answers", { json: answer }),
    activate: () => call(fetcher, "/api/onboarding/activate", { json: {} }),
    testChangeWithAgent: async (id, milliCredits) =>
      (
        await call<{ change: TreeChange }>(fetcher, `/api/tree-changes/${encodeURIComponent(id)}/agent-test`, {
          json: { milliCredits },
        })
      ).change,
    confirmChange: async (id, migrate) =>
      (
        await call<{ applied: AppliedTreeChange }>(fetcher, `/api/tree-changes/${encodeURIComponent(id)}/confirm`, {
          json: { migrate },
        })
      ).applied,
  };
}

/** Paise as Indian rupees with lakh and crore grouping, e.g. 12345678 → "₹1,23,456.78". */
export function rupees(paise: number): string {
  const sign = paise < 0 ? "−" : "";
  const abs = Math.abs(paise);
  return `${sign}₹${indianGrouping(String(Math.trunc(abs / 100)))}.${String(abs % 100).padStart(2, "0")}`;
}

// The Audit Log and Credits, as the Cockpit reads them.

/** One Audit Log entry, as `GET /audit` answers. */
export interface AuditEntry {
  readonly sequence: number;
  readonly source: string;
  readonly action: string;
  readonly run: string | null;
  readonly node: string | null;
  readonly object: { readonly name: string; readonly id: string | null } | null;
  readonly actor: { readonly kind: "person"; readonly id: string } | { readonly kind: "system" };
  readonly agent: string | null;
  readonly occurredAt: string;
  /** What the event carried: for a message, its words exactly as sent or received. */
  readonly detail: Readonly<Record<string, unknown>>;
  /**
   * The names the entry is read by, so no row shows an id (#462): the people it names by id, what its Run is about
   * (a sales order, enquiry or Document by number, its Counterparty or its Value Stream) and its step's name.
   */
  readonly names?: AuditEntryNames;
}

export interface AuditEntryNames {
  readonly people: Readonly<Record<string, string>>;
  readonly about?: { readonly object: string; readonly label: string };
  readonly step?: string;
}

/** Costs in paise. */
export interface Costs {
  readonly material: number;
  readonly conversion: number;
  readonly job_work: number;
  readonly total: number;
}

/** A sales order's cost so far against its estimate, and the margin it is heading for (6.5.4). */
export interface OrderCost {
  readonly salesOrder: string;
  readonly number: string;
  readonly customer: string;
  readonly estimated?: Costs;
  readonly actual: Costs;
  /** The cost so far plus what the BOM and routing cost for what is not booked yet. */
  readonly projected: Costs;
  readonly orderValue?: number;
  readonly quotedMarginPct?: number;
  readonly projectedMarginPct?: number;
  readonly marginPoints: number;
  readonly atRisk: boolean;
  readonly cause: string;
  readonly causes: readonly string[];
}

/** Why a Run is stopped or on whom it waits (#513). */
export interface RunStanding {
  readonly kind: "blocked" | "waiting" | "paused" | "halted";
  /** The Work Item it stands at, by its Task's name. */
  readonly task?: string;
  /** Why, as recorded: a block's problems. */
  readonly reasons: readonly string[];
  readonly pausedBecause?: "credits_exhausted" | "kill_switch" | "read_only";
  /** A Role by name, or LAMP; absent when it waits on the person reading. */
  readonly onWhom?: { readonly role: string } | { readonly lamp: true };
  /** It waits on the person reading: they act on `workItem` from its card. */
  readonly yours: boolean;
  readonly workItem?: string;
  /** LAMP prepared it; it goes on once confirmed. */
  readonly prepared?: true;
}

export type WorkItemStatus = "open" | "awaiting_approval" | "blocked" | "done";

/** A Run's own page, as `GET /api/runs/:run` answers (#513). */
export interface RunPage {
  readonly run: string;
  /** What it is about: "SO/26-27/0003 · Bharat Hydraulics · 80 Hydraulic piston rod". */
  readonly subject?: string;
  readonly valueStream: string | null;
  readonly status: "active" | "blocked" | "paused" | "halted" | "completed";
  readonly step: string | null;
  readonly standing?: RunStanding;
  readonly steps: readonly {
    readonly node: string;
    readonly name: string;
    readonly workItems: readonly {
      readonly name: string;
      readonly status: WorkItemStatus;
      /** What LAMP guessed in doing it, for the person to check. */
      readonly note?: string;
    }[];
  }[];
  /** Whether the person's Roles read the Run's Audit Log entries and Credits: the page asks for them only then. */
  readonly sees: { readonly audit: boolean; readonly credits: boolean };
  /** The order's cost and margin, where the Run has one and the person's Roles see costs and margins. */
  readonly cost?: OrderCost;
  /** The order the Run is about, for anyone who may see the Run and the order: its kept files are listed on the page. */
  readonly order?: { readonly object: "sales_order" | "purchase_order"; readonly id: string };
}

/** What a Run has used, as `GET /runs/:run/credits` answers. */
export interface RunCredits {
  readonly run: string;
  readonly milliCredits: number;
  readonly credits: number;
  readonly steps: number;
  readonly modelCalls: number;
  readonly byNode: Readonly<Record<string, number>>;
}

/** The Audit Log filters the Cockpit offers, as `GET /audit` takes them. */
export const AUDIT_FILTERS = ["run", "object", "objectId", "actor", "agent", "from", "to", "after"] as const;
export type AuditFilters = Partial<Record<(typeof AUDIT_FILTERS)[number], string>>;

/** `filters` as `GET /audit`'s query string, empty filters left out. */
export function auditQuery(filters: AuditFilters, limit?: number): URLSearchParams {
  const query = new URLSearchParams();
  for (const name of AUDIT_FILTERS) {
    const value = filters[name];
    if (value) query.set(name, value);
  }
  if (limit !== undefined) query.set("limit", String(limit));
  return query;
}

/** An answer, or why there is none: signed out (401), not allowed (403), or another failure. */
export type Answer<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly status: number };

export interface CockpitApi {
  auditEntries(filters: AuditFilters): Promise<Answer<readonly AuditEntry[]>>;
  runCredits(run: string): Promise<Answer<RunCredits>>;
}

export interface CockpitApiOptions {
  readonly baseUrl: string;
  readonly fetch?: (input: string, init?: RequestInit) => Promise<Response>;
}

export function cockpitApi({ baseUrl, fetch = globalThis.fetch }: CockpitApiOptions): CockpitApi {
  async function get<T>(path: string): Promise<Answer<T>> {
    const response = await fetch(new URL(path, baseUrl).toString(), { credentials: "include" });
    if (!response.ok) return { ok: false, status: response.status };
    return { ok: true, value: (await response.json()) as T };
  }
  return {
    async auditEntries(filters) {
      const query = auditQuery(filters);
      const search = query.size > 0 ? `?${query.toString()}` : "";
      const answer = await get<{ entries: readonly AuditEntry[] }>(`/audit${search}`);
      return answer.ok ? { ok: true, value: answer.value.entries } : answer;
    },
    runCredits: (run) => get<RunCredits>(`/runs/${encodeURIComponent(run)}/credits`),
  };
}
